import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from '..';
import { DateTime } from 'luxon';
import { TimeLogsByProjectWithLeaveRequestsDto } from '../../commonTypes/acs/timelog.types';
import { restoreAll } from '../restoreAll';
import { PatchTimelogDto, TimeLogDto } from '../../commonTypes/timelog.types';

export interface TimeTrackingState {
    value: {
        timeTrackingTimestamp: string;
        timeTrackingWeekStart: string;
        timeTrackingWeenEnd: string;
        timeLogsByProjectWithLeaveRequests: TimeLogsByProjectWithLeaveRequestsDto;
        timeTrackingView?: TimeTrackingView;
        pendingTimeLogs: TimeLogDto[];
        openedProjectWrappers: string[];
    };
}

export type TimeTrackingView = 'day' | 'week';

interface WeekAction {
    start: string;
    end: string;
}

export interface TimeStampAction {
    day: string;
    start: string;
    end: string;
}

export interface TimeLogToEdit extends PatchTimelogDto {
    id: string;
    projectId: string;
}

export const toTimeStampAction = (day: DateTime) => {
    return {
        day: day.toISO(),
        start: day.startOf('week').toISO(),
        end: day.endOf('week').toISO(),
    };
};

const initialState: TimeTrackingState = {
    value: {
        timeTrackingTimestamp: '1970-01-01T00:00:00.000Z',
        timeTrackingWeekStart: '1970-01-01T00:00:00.000Z',
        timeTrackingWeenEnd: '1970-01-01T23:59:59.999Z',
        timeLogsByProjectWithLeaveRequests: {
            leaveRequests: [],
            timeLogsByProject: [],
        },
        pendingTimeLogs: [],
        openedProjectWrappers: [],
    },
};

const timeTrackingSlice = createSlice({
    name: 'counter',
    initialState,
    extraReducers: builder => builder.addCase(restoreAll, () => initialState),
    reducers: {
        setTimeTrackingTimestamp: (state, action: PayloadAction<TimeStampAction>) => {
            state.value.timeTrackingTimestamp = action.payload.day;
            state.value.timeTrackingWeekStart = action.payload.start;
            state.value.timeTrackingWeenEnd = action.payload.end;
        },
        setTimeTrackingWeek: (state, action: PayloadAction<WeekAction>) => {
            state.value.timeTrackingWeekStart = action.payload.start;
            state.value.timeTrackingWeenEnd = action.payload.end;
            state.value.timeTrackingTimestamp = action.payload.start;
        },
        setTimeLogsByProjectWithLeaveRequests: (state, action: PayloadAction<TimeLogsByProjectWithLeaveRequestsDto>) => {
            state.value.timeLogsByProjectWithLeaveRequests = action.payload;

            if (state.value.pendingTimeLogs.length > 0) {
                for (const prj of action.payload.timeLogsByProject) {
                    for (const tl of prj.timeLogs) {
                        state.value.pendingTimeLogs = state.value.pendingTimeLogs.filter(temp => temp.id !== tl.id);
                    }
                }
            }
        },
        addTimeLogOfProjectForView: (state, action: PayloadAction<TimeLogDto>) => {
            const projectId = action.payload.projectId;

            const timeLogsForProjects = state.value.timeLogsByProjectWithLeaveRequests.timeLogsByProject;
            const timeLogsByProject = timeLogsForProjects.find(timeLogsForProject => timeLogsForProject.projectId === projectId);
            if (timeLogsByProject) {
                const { timeTrackingWeekStart, timeTrackingWeenEnd, timeTrackingTimestamp } = state.value;
                const { logStarted, id } = action.payload;

                const timeLogExist = timeLogsByProject.timeLogs.find(tl => tl.id === id);

                if (state.value.timeTrackingView === 'day') {
                    const isOnDay = DateTime.fromISO(timeTrackingTimestamp).hasSame(DateTime.fromISO(logStarted), 'day');
                    if (isOnDay && !timeLogExist) {
                        timeLogsByProject.timeLogs.push(action.payload);
                        state.value.pendingTimeLogs = state.value.pendingTimeLogs.filter(tl => tl.id !== action.payload.id);
                    }
                } else if (state.value.timeTrackingView === 'week') {
                    const isInWeek =
                        DateTime.fromISO(timeTrackingWeekStart) <= DateTime.fromISO(logStarted) &&
                        DateTime.fromISO(logStarted) <= DateTime.fromISO(timeTrackingWeenEnd);
                    if (isInWeek && !timeLogExist) timeLogsByProject.timeLogs.push(action.payload);
                }
            }
        },
        addTimeLogForProject: (state, action: PayloadAction<TimeLogDto>) => {
            const projectId = action.payload.projectId;

            const timeLogsForProjects = state.value.timeLogsByProjectWithLeaveRequests.timeLogsByProject;
            const timeLogsByProject = timeLogsForProjects.find(timeLogsForProject => timeLogsForProject.projectId === projectId);
            if (timeLogsByProject) {
                const timeLogExist = timeLogsByProject.timeLogs.find(tl => tl.id === action.payload.id);

                if (!timeLogExist) {
                    timeLogsByProject.timeLogs.push(action.payload);
                    state.value.pendingTimeLogs = state.value.pendingTimeLogs.filter(tl => tl.id !== action.payload.id);
                }
            }
        },
        removeTimeLogOfProject: (state, action: PayloadAction<TimeLogDto>) => {
            const timeLogId = action.payload.id;
            const projectId = action.payload.projectId;
            const timeLogsForProjects = state.value.timeLogsByProjectWithLeaveRequests.timeLogsByProject;
            const timeLogsByProject = timeLogsForProjects.find(timeLogsForProject => timeLogsForProject.projectId === projectId);
            if (timeLogsByProject) {
                timeLogsByProject.timeLogs = timeLogsByProject.timeLogs.filter(timeLog => timeLog.id !== timeLogId);
            }
        },
        addSubmissionIntervalOfProject: (state, action) => {
            const projectId = action.payload.projectId;
            const timeLogsByProjects = state.value.timeLogsByProjectWithLeaveRequests.timeLogsByProject;
            const timeLogsByProject = timeLogsByProjects.find(timeLogsByProject => timeLogsByProject.projectId === projectId);
            timeLogsByProject?.submissionIntervals.push(action.payload);
        },
        deleteSubmissionIntervalOfProject: (state, action) => {
            const { id, projectId } = action.payload;
            const timeLogsByProjects = state.value.timeLogsByProjectWithLeaveRequests.timeLogsByProject;

            const timeLogsByProject = timeLogsByProjects.find(timeLogsByProject => timeLogsByProject.projectId === projectId);
            if (!timeLogsByProject) return;

            const idx = timeLogsByProject.submissionIntervals.findIndex(si => si.id === id);
            if (idx !== -1) timeLogsByProject.submissionIntervals.splice(idx, 1);
        },
        editTimeLogOfProject: (state, action: PayloadAction<TimeLogToEdit>) => {
            const timeLogId = action.payload.id;
            const projectId = action.payload.projectId;
            const timeLogsForProjects = state.value.timeLogsByProjectWithLeaveRequests.timeLogsByProject;
            const timeLogsByProject = timeLogsForProjects.find(timeLogsForProject => timeLogsForProject.projectId === projectId);
            const editedTimeLog = timeLogsByProject?.timeLogs.find(timeLog => timeLog.id === timeLogId);
            if (editedTimeLog) {
                editedTimeLog.description = action.payload.description;
                if (action.payload.logStarted) editedTimeLog.logStarted = action.payload.logStarted;
                if (action.payload.logEnded) editedTimeLog.logEnded = action.payload.logEnded;
            }
        },
        setTimeTrackingView: (state, action: PayloadAction<TimeTrackingView>) => {
            state.value.timeTrackingView = action.payload;
        },
        addPendingTimeLog: (state, action: PayloadAction<TimeLogDto>) => {
            state.value.pendingTimeLogs.push(action.payload);
        },
        openProjectWrapper: (state, action: PayloadAction<string>) => {
            const exist = state.value.openedProjectWrappers.find(prj => prj === action.payload);
            if (!exist) state.value.openedProjectWrappers.push(action.payload);
        },
        closeProjectWrapper: (state, action: PayloadAction<string>) => {
            state.value.openedProjectWrappers = state.value.openedProjectWrappers.filter(prj => prj !== action.payload);
        },
    },
});

export const {
    setTimeTrackingTimestamp,
    setTimeTrackingWeek,
    setTimeLogsByProjectWithLeaveRequests,
    addTimeLogOfProjectForView,
    removeTimeLogOfProject,
    addSubmissionIntervalOfProject,
    deleteSubmissionIntervalOfProject,
    editTimeLogOfProject,
    setTimeTrackingView,
    openProjectWrapper,
    closeProjectWrapper,
    addTimeLogForProject,
} = timeTrackingSlice.actions;

export const selectTimeTrackingDate = (state: RootState) => DateTime.fromISO(state.timeTracking.value.timeTrackingTimestamp);

export const selectTimeTrackingWeek = (state: RootState) => ({
    start: DateTime.fromISO(state.timeTracking.value.timeTrackingWeekStart),
    end: DateTime.fromISO(state.timeTracking.value.timeTrackingWeenEnd),
});

export const selectTimeTrackingWeekISO = (state: RootState) => ({
    start: state.timeTracking.value.timeTrackingWeekStart,
    end: state.timeTracking.value.timeTrackingWeenEnd,
});

export const selectLeaveRequests = (state: RootState) => state.timeTracking.value.timeLogsByProjectWithLeaveRequests.leaveRequests;

export const selectTimeLogsForProjects = (state: RootState) => state.timeTracking.value.timeLogsByProjectWithLeaveRequests.timeLogsByProject;

export const getPendingTimeLogs = (state: RootState) => state.timeTracking.value.pendingTimeLogs;

export const getOpenedProjectWrappers = (state: RootState) => state.timeTracking.value.openedProjectWrappers;

export default timeTrackingSlice.reducer;
